import { useState } from 'react'
import { Save, Bell, Shield, Database, Zap } from 'lucide-react'

export default function Settings() {
  const [settings, setSettings] = useState({
    autoScan: true,
    scanInterval: 'daily',
    emailNotifications: true,
    slackNotifications: false,
    autoFix: false,
    complianceThreshold: 90,
  })

  const handleSave = () => {
    // Save settings to backend
    console.log('Saving settings:', settings)
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Settings</h2>
        <p className="mt-1 text-sm text-gray-500">
          Configure governance platform behavior and notifications
        </p>
      </div>

      {/* Settings Sections */}
      <div className="space-y-6">
        {/* Scanning Settings */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-4">
            <Shield className="h-5 w-5 text-gray-400 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">
              Scanning Settings
            </h3>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">
                  Automatic Scanning
                </label>
                <p className="text-xs text-gray-500">
                  Automatically scan applications on schedule
                </p>
              </div>
              <button
                onClick={() => setSettings({ ...settings, autoScan: !settings.autoScan })}
                className={`relative inline-flex h-6 w-11 items-center rounded-full ${
                  settings.autoScan ? 'bg-dii-primary' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                    settings.autoScan ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Scan Interval
              </label>
              <select
                value={settings.scanInterval}
                onChange={(e) => setSettings({ ...settings, scanInterval: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-dii-primary"
              >
                <option value="hourly">Hourly</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
          </div>
        </div>

        {/* Notifications */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-4">
            <Bell className="h-5 w-5 text-gray-400 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">
              Notifications
            </h3>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">
                  Email Notifications
                </label>
                <p className="text-xs text-gray-500">
                  Receive email alerts for critical violations
                </p>
              </div>
              <button
                onClick={() => setSettings({ ...settings, emailNotifications: !settings.emailNotifications })}
                className={`relative inline-flex h-6 w-11 items-center rounded-full ${
                  settings.emailNotifications ? 'bg-dii-primary' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                    settings.emailNotifications ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">
                  Slack Notifications
                </label>
                <p className="text-xs text-gray-500">
                  Send alerts to Slack channel
                </p>
              </div>
              <button
                onClick={() => setSettings({ ...settings, slackNotifications: !settings.slackNotifications })}
                className={`relative inline-flex h-6 w-11 items-center rounded-full ${
                  settings.slackNotifications ? 'bg-dii-primary' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                    settings.slackNotifications ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>
        </div>

        {/* Auto-Fix Settings */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-4">
            <Zap className="h-5 w-5 text-gray-400 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">
              Auto-Fix Settings
            </h3>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">
                  Enable Auto-Fix
                </label>
                <p className="text-xs text-gray-500">
                  Automatically fix violations when possible
                </p>
              </div>
              <button
                onClick={() => setSettings({ ...settings, autoFix: !settings.autoFix })}
                className={`relative inline-flex h-6 w-11 items-center rounded-full ${
                  settings.autoFix ? 'bg-dii-primary' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                    settings.autoFix ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Compliance Threshold (%)
              </label>
              <input
                type="number"
                value={settings.complianceThreshold}
                onChange={(e) => setSettings({ ...settings, complianceThreshold: parseInt(e.target.value) })}
                min="0"
                max="100"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-dii-primary"
              />
              <p className="text-xs text-gray-500 mt-1">
                Trigger alerts when compliance falls below this threshold
              </p>
            </div>
          </div>
        </div>

        {/* Database Status */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-4">
            <Database className="h-5 w-5 text-gray-400 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">
              Database Status
            </h3>
          </div>

          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Connection Status</span>
              <span className="font-medium text-green-600">Connected</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Total Scans</span>
              <span className="font-medium text-gray-900">1,247</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Total Violations</span>
              <span className="font-medium text-gray-900">4,892</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Database Size</span>
              <span className="font-medium text-gray-900">24.3 MB</span>
            </div>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          className="px-4 py-2 bg-dii-primary text-white rounded-md hover:bg-blue-600 flex items-center"
        >
          <Save className="h-4 w-4 mr-2" />
          Save Settings
        </button>
      </div>
    </div>
  )
}